import { defineStore } from 'pinia';
import type {UserType} from "~/types/User";
import type {CookieRef} from "#app";
import {fetchData} from "~/utils/fetchData";
import {endpoints} from "~/constants/endpoints";
import type {
    CheckCredentials, ForgotPasswordCredentials,
    LoginCredentials, OtpCredentials,
    PartRegisterCredentials,
    RegisterCredentials,
    UpdateCredentials
} from "~/types/UserCredentials";
import {RoleEnum} from "~/types/User";
import type {PhoneType} from "~/types/Dictionary";
import type {CityType, RegionType} from "~/types/Geo";
import type {FileType, StrapiFilePostData, StrapiFileType} from "~/types/Files";
import type {PortfolioType} from "~/types/Portfolio";
interface UserStore {
    user: UserType | null
    access_token: CookieRef<string | null> | null
    isAuthenticated: boolean
    guest_role: RoleEnum
    phone_code: PhoneType | null
    region: RegionType | null
    city: string | null
    unread_messages_count: number
    loading: boolean
    geoLocation: {
        lat: number
        lng: number
    } | null
    geoLocationBounds: {
        minLat: number
        maxLat: number
        minLng: number
        maxLng: number
    } | null
    wallets: {
        count: number|null
        limit: number
    }
}

interface FileCredentials {
    files: FileType[]
    field: string
    refId: string | number
    ref: string
}

export interface SocialLoginResponse {
    idToken: string | undefined
    provider: 'google' | 'facebook' | 'apple'
}

export const useUserStore = defineStore('userStore', {
    state: (): UserStore => ({
        user: null,
        access_token: null,
        isAuthenticated: false,
        guest_role: RoleEnum.CUSTOMER,
        phone_code: null,
        region: null,
        city: null,
        unread_messages_count: 0,
        loading: false,
        geoLocation: null,
        geoLocationBounds: null,
        wallets: {
            count: null,
            limit: 50000
        }
    }),
    getters: {
        specifications: (state) => {
            if(!state.user?.portfolios) return []
            return state.user?.portfolios.flatMap(portfolio => portfolio.specifications.map(spec => spec.documentId))
        },
        currentRole: (state) => {
            return state.user ? state.user.user_role : state.guest_role
        },
        currentPhoneCode: (state) => {
            if(state.phone_code) return state.phone_code
            const globalStore = useGlobalStore()
            const dubai_phone_code = globalStore.phone_codes.find(item => item.code === 'AE')
            return dubai_phone_code || globalStore.phone_codes[0]
        },
        currentCity: (state) => {
            const globalStore = useGlobalStore()
            let currentCity = null
            if(state.city) {
                currentCity = globalStore.locations.cities.find(item => item.documentId === state.city)
            }
            else {
                currentCity = globalStore.locations.cities.find(item => item.name === 'Dubai') ||  globalStore.locations?.cities[0]
            }

            if(currentCity) {
                currentCity.slug = currentCity.name.toLowerCase().replace(/ /g, '-')
            }
            
            return currentCity
        },
        isPartialUserData: (state) => {
            return !state.user?.user_role
        },
        unreadResponsesCount: (state) => {
            if(state.user?.user_role === RoleEnum.MASTER){
                return state.user?.unread_master_responses_count || 0
            } else if(state.user?.user_role === RoleEnum.CUSTOMER){
                return state.user?.unread_customer_responses_count || 0
            }
            return 0
        }
    },
    actions: {
        init() {
            const route = useRoute()

            const tokenCookie = useCookie('access_token', {
                maxAge: 7 * 24 * 60 * 60,
                path: '/',
                secure: true,
                sameSite: 'none',
            });
            this.access_token = tokenCookie.value || null;

            // Устанавливаем city только если оно еще не установлено
            if (!this.city) {
                const cityCookie = useCookie('city', {
                    path: '/',
                    secure: true,
                    sameSite: 'none',
                });
                this.city = cityCookie.value || null;
            }

            if (this.access_token) {
                // Вызываем restoreState без await, но обрабатываем ошибки
                this.restoreState().catch((error) => {
                    // Ошибка уже обработана внутри restoreState (состояние очищено)
                    // Просто логируем для отладки, если нужно
                });
            }
        },
        async check(credentials: CheckCredentials) {
            this.loading = true;
            return await fetchData(endpoints.auth.check, {
                method: "GET",
                params: credentials
            }).then(({data, meta}) => {
                return data;
            }).catch((e) => {
                return Promise.reject(e);
            }).finally(() => {
                this.loading = false;
            });
        },

        async login(credentials: LoginCredentials) {
            this.loading = true;
            return await fetchData<any>(endpoints.auth.login, {
                method: "POST",
                body: credentials
            }).then(async ({data, meta}) => {
                this.setToken(data?.token)
                if(data?.user?.user_role || data?.user?.user_roles?.length){
                    this.isAuthenticated = true
                    await this.setUser(data?.user)
                } else {
                    await nextTick()
                    await this.fetchUser()
                }
                return data
            }).catch((e) => {
                return Promise.reject(e);
            }).finally(() => {
                this.loading = false;
            });
        },

        async loginWithSocial(request: SocialLoginResponse) {
            const userStore = useUserStore()

            await fetchData(endpoints.auth.social, {
                method: 'POST',
                body: request
            }).then(async ({data, meta}) => {
                console.log(data);
                
                if(data?.token){
                    userStore.setToken(data.token)
                }
                if(data?.user){
                    userStore.isAuthenticated = true
                    await nextTick()
                    await userStore.fetchUser()
                }
            }).catch((e) => {
                return Promise.reject(e);
            }).finally(() => {
                this.loading = false;
            });
        },

        async register(credentials: RegisterCredentials) {
            this.loading = true;
            return await fetchData<any>(endpoints.auth.register, {
                method: "POST",
                body: credentials
            }).then(({data, meta}) => {
                this.setToken(data?.token)
                if(data?.user){
                    this.isAuthenticated = true
                    this.setUser(data?.user)
                }
                const gtm = useGtm()
                if(gtm){
                    gtm.trackEvent({
                        event: 'user_registration',
                        category: 'custom_actions',
                        action: 'submit',
                        label: 'Регистрация пользователя',
                        noninteraction: false,
                        status: true
                    })
                }
                return data
            }).catch((e) => {
                return Promise.reject(e);
            }).finally(() => {
                this.loading = false;
            });
        },

        async update(credentials: UpdateCredentials) {
            if(!this.user?.documentId) return
            this.loading = true;
            credentials.accept_rules_date = new Date().toISOString().split('T')[0]
            if(credentials?.avatar){
                if(this.user.avatar){
                    await fetchAuth(endpoints.files.delete(this.user.avatar.id), {
                        method: "DELETE",
                    }).then(({data, meta}) => {
                        //this.user?.avatar = null;
                    })
                }
                await this.postFile({
                    field: 'avatar',
                    ref: 'plugin::users-permissions.user',
                    refId: this.user.id,
                    files: credentials.avatar
                })
                delete credentials.avatar
            }
            return await fetchAuth<any>(endpoints.user.updateById(this.user.documentId), {
                method: "PUT",
                body: credentials,
            }).then(({data, meta}) => {
                if(data){
                    this.setUser(data)
                }
                return data
            }).catch((e) => {
                return Promise.reject(e);
            }).finally(() => {
                this.loading = false;
            });
        },

        async updateAcceptRulesDate() {
            if(!this.user?.documentId) return
            this.loading = true;
            return await fetchAuth<any>(endpoints.user.updateById(this.user.documentId), {
                method: "PUT",
                body: {
                    accept_rules_date: new Date().toISOString().split('T')[0]
                },
            }).then(({data, meta}) => {
                if(data){
                    this.setUser(data)
                }
                return data
            }).catch((e) => {
                return Promise.reject(e);
            }).finally(() => {
                this.loading = false;
            });
        },

        async updateRole(newRole: RoleEnum) {
            if(!this.user?.documentId) return

            return await fetchAuth<any>(endpoints.user.updateById(this.user.documentId), {
                method: "PUT",
                body: {
                    user_role: newRole
                },
            }).then(({data, meta}) => {
                if(data){
                    this.setUser(data)
                }
                return data
            }).catch((e) => {
                return Promise.reject(e);
            }).finally(() => {
                this.loading = false;
            });
        },

        async partRegister(credentials: PartRegisterCredentials) {
            this.loading = true;
            return await fetchData(endpoints.auth.partRegister, {
                method: "POST",
                data: credentials
            }).then(({data, meta}) => {
                return data;
            }).catch((e) => {
                return Promise.reject(e);
            }).finally(() => {
                this.loading = false;
            });
        },

        async setPassword(credentials: any) {
            this.loading = true;
            return await fetchData(endpoints.auth.setPassword, {
                method: "POST",
                body: credentials
            }).then(({data, meta}) => {
                this.setToken(data?.token)
                if(data?.user){
                    this.isAuthenticated = true
                    this.setUser(data?.user)
                }
                return data
            }).catch((e) => {
                return Promise.reject(e);
            }).finally(() => {
                this.loading = false;
            });
        },

        async forgotPassword(credentials: ForgotPasswordCredentials) {
            this.loading = true;
            return await fetchData(endpoints.auth.forgotPassword, {
                method: "POST",
                data: credentials
            }).then(({data, meta}) => {
                return data;
            }).catch((e) => {
                return Promise.reject(e);
            }).finally(() => {
                this.loading = false;
            });
        },

        async logout() {
            this.loading = true;
            
            const clearState = () => {
                // Очищаем все состояние пользователя
                this.setToken(null)
                this.user = null
                this.isAuthenticated = false
                this.unread_messages_count = 0
                this.wallets.count = null
                
                // Очищаем socket соединение
                if (process.client) {
                    const { cleanup } = useSocket()
                    cleanup()
                }
                
                // Сбрасываем loading после очистки
                this.loading = false
                
                // Используем window.location для полной перезагрузки страницы
                // Это гарантирует полную очистку состояния и кэша
                if (process.client) {
                    window.location.href = '/'
                }
            }
            
            let canCallApi = false
            try {
                useNuxtApp()
                canCallApi = true
            } catch {
                canCallApi = false
            }
            
            if (canCallApi) {
                try {
                    await fetchAuth(endpoints.auth.logout, {
                        method: "POST"
                    }).finally(() => {
                        clearState()
                    });
                } catch (error) {
                    clearState()
                } 
            } else {
                clearState()
            }
        },

        async storePortfolio(credentials: PortfolioType) {
            const portfolioId = credentials?.documentId;
            const { images, ...restCredentials } = credentials;

            const endpoint = portfolioId
                ? endpoints.user.portfolio.update(portfolioId)
                : endpoints.user.portfolio.create;

            delete restCredentials.documentId;
            // Сначала удаляем помеченные на удаление файлы
            if (images.toDelete.length) {
                await this.deleteFiles(images.toDelete);
            }

            const portfolio = await fetchAuth<PortfolioType>(endpoint, {
                method: portfolioId ? 'PUT' : "POST",
                body: {
                    ...restCredentials
                }
            }).then(({data, meta}) => {
                this.user.portfolios = [data];
                const gtm = useGtm()
                if(gtm){
                    gtm.trackEvent({
                        event: 'portfolio_completed',
                        category: 'custom_actions',
                        action: 'submit',
                        label: 'Портфолио заполненно успешно',
                        noninteraction: false,
                    })
                }
                return data;
            }).catch((e) => {
                return Promise.reject(e);
            });

            // Загружаем новые файлы
            if (portfolio && images.new.length) {
                const files = await this.postFile({
                    field: 'images',
                    ref: 'api::portfolio.portfolio',
                    refId: portfolio.id,
                    files: images.new
                }).then((data) => {
                    console.log(data)
                });


            }

            return portfolio;
        },

        async fetchUser() {
            if (!this.access_token) return
            this.loading = true
            try {
                const {data: user, meta} = await fetchAuth<UserType>(endpoints.user.get, {
                    method: 'GET'
                })
                if(user){
                    this.isAuthenticated = true
                    await this.setUser(user as UserType)
                }
            } catch (error) {
                console.error('Error fetching user:', error)
                /* await this.logout() */
            } finally {
                this.loading = false
            }
        },

        async restoreState() {
            if (!this.access_token) {
                this.isAuthenticated = false;
                this.user = null;
                return;
            }

            try {
                await this.fetchUser();
                // Проверяем, что данные пользователя действительно загружены
                if (!this.user) {
                    // Если пользователь не загружен, очищаем состояние без ошибки
                    this.isAuthenticated = false;
                    this.user = null;
                    this.setToken(null);
                    return;
                }
            } catch (error) {
                // При любой ошибке очищаем состояние
                // Не логируем ошибку, так как это нормальное поведение при истекшем токене
                this.isAuthenticated = false;
                this.user = null;
                this.setToken(null);
                // Не выбрасываем ошибку обратно, так как состояние уже очищено
                // Это предотвращает unhandled rejection при вызове из init()
            }
        },

        setRole(role: RoleEnum){
            if(!this.user) this.guest_role = role
            if(this.user?.user_roles.some(item => item === role)) this.user.user_role = role
        },

        setPhoneCode(phoneCode: PhoneType){
            this.phone_code = phoneCode
        },

        setRegion(region: RegionType){
            this.region = region
        },

        setCity(city: CityType){
            if (process.client) {
                const cityCookie = useCookie('city', {
                    path: '/',
                    secure: true,
                    sameSite: 'none',
                });
                cityCookie.value = city.documentId;
            }
            this.city = city.documentId
        },

        updateUserPartial(partialData: Partial<UserType>) {
            if (!this.user || !partialData) return;

            const updatedUser = { ...this.user, ...partialData };

            if (partialData.user_role && typeof partialData.user_role === 'object') {
                updatedUser.user_role = partialData.user_role.name;
            } else if(partialData?.user_roles?.length){
                updatedUser.user_role = partialData?.user_roles[0].name;
            }

            this.user = updatedUser;
        },

        async setUser(payload: UserType) {
            if (!payload) return null;

            const updatedUser = { ...payload };

            if (payload.user_role && typeof payload.user_role === 'object') {
                updatedUser.user_role = payload.user_role.name;
            } else if(payload?.user_roles?.length){
                updatedUser.user_role = payload?.user_roles[0].name;
            }

            this.user = updatedUser;
            //this.getWalletsCount()

            if(process.client && this.access_token){
                const eventBus = useEventBus()
                setTimeout(() => {
                    eventBus.emit('maybeOpenJobtokenOnboarding')
                    eventBus.emit('maybeOpenAcceptRulesModal')
                }, 100)

                // Ждем следующего тика, чтобы убедиться что плагин socket.io загружен
                await nextTick()
                const {connect, onUnreadMessages} = useSocket()
                if (connect) {
                    connect(this.access_token)
                }
                if (onUnreadMessages) {
                    onUnreadMessages((unreadMessages) => {
                        this.unread_messages_count = unreadMessages || 0
                    })
                }
            }

            return updatedUser;
        },

        async getWalletsCount(){
            // Проверяем доступность контекста Nuxt перед использованием composables
            let canCallApi = false
            try {
                useNuxtApp()
                canCallApi = true
            } catch {
                canCallApi = false
            }
            
            if (!canCallApi) {
                return Promise.reject(new Error('Nuxt context not available'))
            }
            
            const config = useRuntimeConfig()
            return await $fetch(endpoints.user.jobtoken.count, {
                baseURL: config.public.baseURL,
                method: 'GET',
            }).then((count) => {
                if(count && typeof count === 'number'){
                    this.wallets.count = count
                }
                return count
            }).catch((e) => {
                return Promise.reject(e);
            })
        },

        async updateJobTokenWallet(wallet: string){
            const eventBus = useEventBus()
            return await fetchAuth(endpoints.user.jobtoken.updateWallet, {
                method: 'PUT',
                body: {
                    walletAddress: wallet,
                },
            }).then(({data, meta}) => {
                const hasJobtokenWallet = !!this.user?.jobtoken_wallet
                if(data?.jobtoken_wallet){
                    this.user.jobtoken_wallet = data.jobtoken_wallet
                    if(!hasJobtokenWallet) {
                        setTimeout(() => {
                            eventBus.emit('maybeOpenJobtokenSuccess')
                        }, 500)
                    }
                }
                
                return data
            }).catch((e) => {
                return Promise.reject(e);
            })
        },
        
        setToken(token: string | null){
            // Обновляем состояние всегда
            this.access_token = token;
            
            // Обновляем куку только если контекст Nuxt доступен
            try {
                const tokenCookie = useCookie('access_token', {
                    maxAge: token ? 7 * 24 * 60 * 60 : -1, // -1 удаляет cookie
                    path: '/',
                    secure: true,
                    sameSite: 'none',
                });
                if (token) {
                    tokenCookie.value = token;
                } else {
                    // Явно удаляем cookie при logout
                    tokenCookie.value = null;
                    // Дополнительно удаляем через прямое обращение к document.cookie на клиенте
                    if (process.client) {
                        document.cookie = 'access_token=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=None; Secure';
                    }
                }
            } catch (error) {
                // Если useCookie не доступен (вне контекста Nuxt), 
                // просто обновляем состояние, кука будет обновлена при следующем доступе через init()
                // Это нормальное поведение, когда метод вызывается из обработчика ошибок вне контекста
            }
        },

        async postFile(postData: StrapiFilePostData){
            const formData = new FormData();

            // Добавляем мета-данные
            formData.append('ref', postData.ref);
            formData.append('refId', postData.refId.toString());
            formData.append('field', postData.field);
            // Добавляем файлы
            if (Array.isArray(postData.files)) {
                console.log(postData.files)
                postData.files.forEach(file => {
                    formData.append('files', file);
                });
            } else {
                formData.append('files', postData.files);
            }

            return await fetchAuth<StrapiFileType[]>(endpoints.files.post, {
                method: "POST",
                body: formData
            }).then(({data, meta}) => {
                return data
            }).catch((e) => {
                return Promise.reject(e);
            })
        },

        async deleteFiles(fileIds: number[]){
            const deletePromises = fileIds.map(id =>
                fetchAuth(endpoints.files.delete(id), {
                    method: 'DELETE'
                }).then(({data, meta}) => data)
            );

            return Promise.all(deletePromises);
        },

        async deleteAccount(values: {
            delete_reason: string
        }) {
            if(!this.user?.documentId) return
            return await fetchAuth(endpoints.user.delete(this.user?.documentId), {
                method: 'PUT',
                body: {
                    delete_reason: values.delete_reason,
                    is_deleted: true
                }
            }).then(({data, meta}) => {
                this.logout()
                return true
            }).catch((e) => {
                return Promise.reject(e);
            })
        }
    },
});
